import React, { useEffect, useState } from "react";
import { FaEye, FaFilter, FaTimes } from "react-icons/fa";
import CompactActionBar from "../components/CompactActionBar";
import SharedFilter from "../components/SharedFilter";
import { useAuth } from "../contexts/AuthContext";
import "../css/pages/CancelledBookings.css";
import { usePageTitle } from "../hooks/usePageTitle";
import bookingService, { Booking } from "../services/bookingService";
import { Project, projectService } from "../services/projectService";
import { unitService } from "../services/unitService";
import { formatDate } from "../utils/formatters";
import { formatUnitDisplay } from "../utils/unitSearch";

const CancelledBookings: React.FC = () => {
  usePageTitle();
  const { user } = useAuth();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [wings, setWings] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingBooking, setViewingBooking] = useState<Booking | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterProject, setFilterProject] = useState("");
  const [filterWing, setFilterWing] = useState("");
  const [filterUnit, setFilterUnit] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    project: "",
    wing: "",
    unit: "",
    customer: "",
    mobile: "",
    dateFrom: "",
    dateTo: "",
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(100);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (projects.length > 0) {
      loadBookings();
    }
  }, [projects, currentPage, appliedFilters, limit]);

  const loadInitialData = async () => {
    try {
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);
      // Set default project from user's first project
      if (user?.userProjects?.length) {
        const defaultProjectId = user.userProjects[0].projectId.toString();
        setFilterProject(defaultProjectId);
        setAppliedFilters(prev => ({ ...prev, project: defaultProjectId }));
      }
    } catch (err) {
      setError("Failed to load initial data");
    }
  };

  // Fetch wings when project filter changes
  useEffect(() => {
    const fetchWings = async () => {
      if (filterProject) {
        const projectWings = await unitService.getWingsByProject(parseInt(filterProject));
        setWings(projectWings);
      } else {
        setWings([]);
      }
      setFilterWing('');
    };
    fetchWings();
  }, [filterProject]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      project: filterProject,
      wing: filterWing,
      unit: filterUnit,
      customer: filterCustomer,
      mobile: filterMobile,
      dateFrom: filterDateFrom,
      dateTo: filterDateTo,
    });
    setCurrentPage(1);
    setShowFilters(false);
  };

  const clearAllFilters = () => {
    setFilterProject("");
    setFilterWing("");
    setFilterUnit("");
    setFilterCustomer("");
    setFilterMobile("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setAppliedFilters({
      project: "",
      wing: "",
      unit: "",
      customer: "",
      mobile: "",
      dateFrom: "",
      dateTo: "",
    });
    setCurrentPage(1);
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      // Parse unit filter for wing+unitNo
      let unitWing = appliedFilters.wing || undefined;
      let unitNo = undefined;
      if (appliedFilters.unit) {
        const dashMatch = appliedFilters.unit.match(/^([A-Za-z]+)-(.+)$/);
        if (dashMatch) {
          unitWing = dashMatch[1];
          unitNo = dashMatch[2];
        } else {
          unitNo = appliedFilters.unit;
        }
      }
      const data = await bookingService.getBookings({
        page: currentPage,
        limit: limit,
        status: "cancelled",
        projectId: appliedFilters.project ? parseInt(appliedFilters.project) : undefined,
        wing: unitWing,
        unitNo: unitNo,
        memberName: appliedFilters.customer || undefined,
        mobile: appliedFilters.mobile || undefined,
        cancelledDateFrom: appliedFilters.dateFrom || undefined,
        cancelledDateTo: appliedFilters.dateTo || undefined,
      });
      setBookings(data.bookings);
      setTotalPages(data.pagination.totalPages);
      setTotalRecords(data.pagination.total);
    } catch (err) {
      setError("Failed to load cancelled bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (booking: Booking) => {
    try {
      const fullBooking = await bookingService.getBookingById(booking.id);
      setViewingBooking(fullBooking);
      setShowViewModal(true);
    } catch (err) {
      setError("Failed to load booking details");
    }
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    const half = Math.floor(maxVisible / 2);

    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  const extractCancellationReason = (remarks: string): string => {
    if (!remarks) return "No reason provided";

    const reasonMatch = remarks.match(/Cancellation Reason: (.+)$/);
    return reasonMatch ? reasonMatch[1].trim() : "No specific reason provided";
  };

  return (
    <div className="page-container">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError("")} className="error-close">
            ×
          </button>
        </div>
      )}

      {/* Action Bar */}
      <CompactActionBar>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1 }}>
          <button
            className={`filter-toggle-btn ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Project:</span>
            <select
              value={filterProject}
              onChange={(e) => {
                setFilterProject(e.target.value);
                setAppliedFilters(prev => ({ ...prev, project: e.target.value }));
                setCurrentPage(1);
              }}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value="">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Show:</span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={400}>400</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                padding: "6px 12px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                background: currentPage === 1 ? "#f9fafb" : "white",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                fontSize: "13px",
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: "6px 12px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                background: currentPage === totalPages ? "#f9fafb" : "white",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                fontSize: "13px",
              }}
            >
              Next
            </button>
          </div>
        )}
      </CompactActionBar>

      {/* Filters Panel */}
      {showFilters && (
        <SharedFilter
          wings={wings}
          wing={filterWing}
          onWingChange={setFilterWing}
          unitNumber={filterUnit}
          onUnitNumberChange={setFilterUnit}
          customerName={filterCustomer}
          onCustomerNameChange={setFilterCustomer}
          mobile={filterMobile}
          onMobileChange={setFilterMobile}
          dateFrom={filterDateFrom}
          onDateFromChange={setFilterDateFrom}
          dateTo={filterDateTo}
          onDateToChange={setFilterDateTo}
          dateLabel="Cancellation Date"
          onApply={() => {
            handleApplyFilters();
            setCurrentPage(1);
          }}
          onClear={clearAllFilters}
        />
      )}

      {/* Cancelled Bookings Table */}
      <div style={{ background: '#fff', margin: 0, padding: 0 }}>
        {loading ? (
          <div
            style={{ textAlign: "center", padding: "60px", color: "#6b7280" }}
          >
            <div style={{ fontSize: "16px" }}>
              Loading cancelled bookings...
            </div>
          </div>
        ) : (
          <table className="standard-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Customer</th>
                <th>Project</th>
                <th>Unit</th>
                <th>Unit Type</th>
                <th>Booking Date</th>
                <th>Cancelled Date</th>
                <th>Cancellation Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking, index) => {
                const primaryApplicant =
                  booking.applicants.find((a) => a.isPrimary) ||
                  booking.applicants[0];
                return (
                  <tr key={booking.id}>
                    <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                      {totalRecords - ((currentPage - 1) * limit) - index}
                    </td>
                    <td>
                      <div className="customer-info">
                        <div className="customer-name">
                          {primaryApplicant
                            ? `${primaryApplicant.firstName} ${primaryApplicant.surname}`
                            : "N/A"}
                        </div>
                        <div className="customer-contact">
                          {primaryApplicant?.contactNumber}
                        </div>
                      </div>
                    </td>
                    <td>
                      {booking.unit?.project?.name ||
                        booking.project?.name ||
                        "N/A"}
                    </td>
                    <td>
                      <span style={{ fontWeight: 600 }}>
                        {formatUnitDisplay(booking.unit.wing, booking.unit.unitNo)}
                      </span>
                    </td>
                    <td>{booking.unit.unitType}</td>
                    <td>{formatDate(booking.bookingDate)}</td>
                    <td>{formatDate(booking.updatedAt)}</td>
                    <td className="cancellation-reason">
                      {extractCancellationReason(booking.remarks || "")}
                    </td>
                    <td className="actions">
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => handleView(booking)}
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {bookings.length === 0 && !loading && (
          <div className="no-data">
            <FaTimes />
            <p>No cancelled bookings found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing page {currentPage} of {totalPages} ({bookings.length}{" "}
            bookings)
          </div>
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            {generatePageNumbers().map((page) => (
              <button
                key={page}
                className={`pagination-btn ${
                  currentPage === page ? "active" : ""
                }`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="pagination-btn"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingBooking && (
        <div className="modal-overlay">
          <div className="modal view-modal">
            <div className="modal-header">
              <h2>Cancelled Booking Details - {viewingBooking.bookingId}</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowViewModal(false);
                  setViewingBooking(null);
                }}
              >
                ×
              </button>
            </div>

            <div className="booking-details">
              {/* Cancellation Notice */}
              <div className="cancellation-notice">
                <FaTimes className="cancel-icon" />
                <div>
                  <h3>This booking has been cancelled</h3>
                  <p>Cancelled on: {formatDate(viewingBooking.updatedAt)}</p>
                  <p>
                    <strong>Reason:</strong>{" "}
                    {extractCancellationReason(viewingBooking.remarks || "")}
                  </p>
                </div>
              </div>

              {/* Basic Info */}
              <div className="details-section">
                <h3>Basic Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Booking ID:</label>
                    <span>{viewingBooking.bookingId}</span>
                  </div>
                  <div className="info-item">
                    <label>Project:</label>
                    <span>
                      {viewingBooking.unit?.project?.name ||
                        viewingBooking.project?.name ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Unit:</label>
                    <span>
                      {viewingBooking.unit.unitNo} (
                      {viewingBooking.unit.unitType})
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Original Booking Date:</label>
                    <span>{formatDate(viewingBooking.bookingDate)}</span>
                  </div>
                  <div className="info-item">
                    <label>Source:</label>
                    <span>{viewingBooking.source}</span>
                  </div>
                  {viewingBooking.broker && (
                    <div className="info-item">
                      <label>Broker:</label>
                      <span>
                        {viewingBooking.broker.name} (
                        {viewingBooking.broker.mobile})
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Details */}
              <div className="details-section">
                <h3>Customer Details</h3>
                <div className="applicants-list">
                  {viewingBooking.applicants.map((applicant) => (
                    <div key={applicant.id} className="applicant-card">
                      <div className="applicant-header">
                        <h4>
                          {applicant.firstName}{" "}
                          {applicant.fatherHusbandName &&
                            `S/o ${applicant.fatherHusbandName}`}{" "}
                          {applicant.surname}
                          {applicant.isPrimary && (
                            <span className="primary-badge">Primary</span>
                          )}
                        </h4>
                      </div>
                      <div className="applicant-contact">
                        {applicant.contactNumber}
                      </div>
                    </div>
                  ))}
                </div>

                {viewingBooking.customerAddress && (
                  <div className="info-item">
                    <label>Address:</label>
                    <span>{viewingBooking.customerAddress}</span>
                  </div>
                )}
              </div>

              {/* Unit Details */}
              <div className="details-section">
                <h3>Unit Details</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Unit Number:</label>
                    <span>{viewingBooking.unit.unitNo}</span>
                  </div>
                  <div className="info-item">
                    <label>Type:</label>
                    <span>{viewingBooking.unit.unitType}</span>
                  </div>
                  <div className="info-item">
                    <label>Size:</label>
                    <span>{viewingBooking.unit.size}</span>
                  </div>
                  <div className="info-item">
                    <label>Wing:</label>
                    <span>{viewingBooking.unit.wing}</span>
                  </div>
                  <div className="info-item">
                    <label>Status:</label>
                    <span className="unit-status available">
                      Available for Booking
                    </span>
                  </div>
                </div>
              </div>

              {/* Full Remarks */}
              {viewingBooking.remarks && (
                <div className="details-section">
                  <h3>Remarks & Cancellation Details</h3>
                  <p className="remarks-text">{viewingBooking.remarks}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowViewModal(false);
                  setViewingBooking(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CancelledBookings;
